import React, { useState, useRef } from "react";
import axios from "axios";
import styles from "./WellUploadsComponents.module.css";

export const WellUploadPage = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedEquipmentFile, setSelectedEquipmentFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isEquipmentUploading, setIsEquipmentUploading] = useState(false);
  const [isEquipmentDownloading, setIsEquipmentDownloading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [equipmentErrorMessage, setEquipmentErrorMessage] = useState("");
  const [equipmentSuccessMessage, setEquipmentSuccessMessage] = useState("");
  const fileInputRef = useRef(null);
  const equipmentFileInputRef = useRef(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleEquipmentFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedEquipmentFile(file);
    setEquipmentErrorMessage("");
    setEquipmentSuccessMessage("");
  };

  const downloadTemplate = async () => {
    try {
      setIsDownloading(true);
      setErrorMessage("");
      setSuccessMessage("");

      const response = await axios.get(
        "/api/sampling_event/create_well_data_upload_template",
        {
          responseType: "blob",
        }
      );

      // Create a URL for the blob and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "well_data_upload_template.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccessMessage("Template downloaded successfully!");
    } catch (error) {
      console.error("Error downloading template:", error);
      setErrorMessage("Failed to download template. Please try again later.");
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadEquipmentTemplate = async () => {
    try {
      setIsEquipmentDownloading(true);
      setEquipmentErrorMessage("");
      setEquipmentSuccessMessage("");

      const response = await axios.get(
        "/api/sampling_event/create_equipment_data_upload_template",
        {
          responseType: "blob",
        }
      );

      // Create a URL for the blob and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "equipment_data_upload_template.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setEquipmentSuccessMessage("Equipment template downloaded successfully!");
    } catch (error) {
      console.error("Error downloading equipment template:", error);
      setEquipmentErrorMessage(
        "Failed to download equipment template. Please try again later."
      );
    } finally {
      setIsEquipmentDownloading(false);
    }
  };

  const uploadWellData = async () => {
    if (!selectedFile) {
      setErrorMessage("Please select a file to upload.");
      return;
    }

    // Check if file is a CSV
    if (!selectedFile.name.endsWith(".csv")) {
      setErrorMessage("Please upload a CSV file.");
      return;
    }

    try {
      setIsUploading(true);
      setErrorMessage("");
      setSuccessMessage("");

      const formData = new FormData();
      formData.append("wellsDataFile", selectedFile);

      const response = await axios.post(
        "/api/sampling_event/well_data_upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        setSuccessMessage("Well data uploaded successfully!");
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Error uploading well data:", error);
      setErrorMessage(
        error.response?.data?.error ||
          "Failed to upload well data. Please check your file format and try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

  const uploadEquipmentData = async () => {
    if (!selectedEquipmentFile) {
      setEquipmentErrorMessage("Please select a file to upload.");
      return;
    }

    // Check if file is a CSV
    if (!selectedEquipmentFile.name.endsWith(".csv")) {
      setEquipmentErrorMessage("Please upload a CSV file.");
      return;
    }

    try {
      setIsEquipmentUploading(true);
      setEquipmentErrorMessage("");
      setEquipmentSuccessMessage("");

      const formData = new FormData();
      formData.append("equipmentDataFile", selectedEquipmentFile);

      const response = await axios.post(
        "/api/sampling_event/equipment_data_upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        setEquipmentSuccessMessage("Equipment data uploaded successfully!");
        setSelectedEquipmentFile(null);
        if (equipmentFileInputRef.current) {
          equipmentFileInputRef.current.value = "";
        }
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Error uploading equipment data:", error);
      setEquipmentErrorMessage(
        error.response?.data?.error ||
          "Failed to upload equipment data. Please check your file format and try again."
      );
    } finally {
      setIsEquipmentUploading(false);
    }
  };

  return (
    <div className={styles.wellUploadPageContainer}>
      <div className={styles.wellUploadContainer}>
        <h1 className={styles.pageTitle}>Data Management</h1>

        {/* Well Data Section */}
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Well Data Management</h2>

          {/* Template Download Section */}
          <div className={styles.subSectionContainer}>
            <h3 className={styles.subSectionTitle}>Download Template</h3>
            <p className={styles.description}>
              Download a CSV template to prepare your well data for upload. The
              template contains all required fields and formatting guidelines.
            </p>
            <div className={styles.buttonContainer}>
              <button
                className={styles.actionButton}
                onClick={downloadTemplate}
                disabled={isDownloading}
              >
                {isDownloading ? "Downloading..." : "Download Template"}
              </button>
            </div>
          </div>

          {/* Well Data Upload Section */}
          <div className={styles.subSectionContainer}>
            <h3 className={styles.subSectionTitle}>Upload Well Data</h3>
            <p className={styles.description}>
              Upload your completed CSV file containing well data. Make sure to
              follow the template format to ensure successful data import.
            </p>

            <div className={styles.fileInputContainer}>
              <input
                type="file"
                accept=".csv"
                className={styles.fileInput}
                id="wellDataFile"
                onChange={handleFileChange}
                ref={fileInputRef}
              />
              <label htmlFor="wellDataFile" className={styles.fileInputLabel}>
                Select CSV File
              </label>
              {selectedFile && (
                <div className={styles.fileNameDisplay}>
                  Selected file: {selectedFile.name}
                </div>
              )}
            </div>

            <div className={styles.buttonContainer}>
              <button
                className={styles.actionButton}
                onClick={uploadWellData}
                disabled={!selectedFile || isUploading}
              >
                {isUploading ? "Uploading..." : "Upload Well Data"}
              </button>
            </div>
          </div>

          {/* Message Display */}
          {errorMessage && (
            <div className={styles.errorMessage}>{errorMessage}</div>
          )}
          {successMessage && (
            <div className={styles.successMessage}>{successMessage}</div>
          )}
        </div>

        {/* Equipment Data Section */}
        <div className={styles.sectionContainer}>
          <h2 className={styles.sectionTitle}>Equipment Data Management</h2>

          {/* Equipment Template Download Section */}
          <div className={styles.subSectionContainer}>
            <h3 className={styles.subSectionTitle}>
              Download Equipment Template
            </h3>
            <p className={styles.description}>
              Download a CSV template to prepare your equipment calibration data
              for upload. The template contains all required fields for
              equipment meters and calibration parameters.
            </p>
            <div className={styles.buttonContainer}>
              <button
                className={styles.actionButton}
                onClick={downloadEquipmentTemplate}
                disabled={isEquipmentDownloading}
              >
                {isEquipmentDownloading
                  ? "Downloading..."
                  : "Download Equipment Template"}
              </button>
            </div>
          </div>

          {/* Equipment Data Upload Section */}
          <div className={styles.subSectionContainer}>
            <h3 className={styles.subSectionTitle}>Upload Equipment Data</h3>
            <p className={styles.description}>
              Upload your completed CSV file containing equipment and
              calibration data. Make sure to follow the template format to
              ensure successful data import.
            </p>

            <div className={styles.fileInputContainer}>
              <input
                type="file"
                accept=".csv"
                className={styles.fileInput}
                id="equipmentDataFile"
                onChange={handleEquipmentFileChange}
                ref={equipmentFileInputRef}
              />
              <label
                htmlFor="equipmentDataFile"
                className={styles.fileInputLabel}
              >
                Select CSV File
              </label>
              {selectedEquipmentFile && (
                <div className={styles.fileNameDisplay}>
                  Selected file: {selectedEquipmentFile.name}
                </div>
              )}
            </div>

            <div className={styles.buttonContainer}>
              <button
                className={styles.actionButton}
                onClick={uploadEquipmentData}
                disabled={!selectedEquipmentFile || isEquipmentUploading}
              >
                {isEquipmentUploading
                  ? "Uploading..."
                  : "Upload Equipment Data"}
              </button>
            </div>
          </div>

          {/* Equipment Message Display */}
          {equipmentErrorMessage && (
            <div className={styles.errorMessage}>{equipmentErrorMessage}</div>
          )}
          {equipmentSuccessMessage && (
            <div className={styles.successMessage}>
              {equipmentSuccessMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WellUploadPage;
